<script lang="ts">
	import { cn } from "#lib/web-ui/ui/utils.js";
	import { Button } from "#lib/web-ui/ui/button/index.js";
	import {
		FooterWrapper,
		FooterRow,
		FooterContactColumn,
		PodcastPlayer
	} from "#lib/web-ui/blocks/index.js";
	import { FaviconMeta } from "#lib/web-ui/meta/index.js";
	import AlignJustifyIcon from "@lucide/svelte/icons/align-justify";
	import XIcon from "@lucide/svelte/icons/x";
	import ChevronDownIcon from "@lucide/svelte/icons/chevron-down";
	import { page } from "$app/state";
	import type { LayoutProps } from "./$types";
	import { cloudinaryUrl } from "#lib/shared/cloudinary.js";
	import { PUBLIC_NODE_ENV } from "$app/env/public";
	import { partytownSnippet } from "@qwik.dev/partytown/integration";

	const partytownScript = "<script>" + partytownSnippet() + "<\/script>";

	let { children, data }: LayoutProps = $props();

	let mobileMenuOpen = $state(false);
	let mobileMediaOpen = $state(false);

	function isActive(href: string): boolean {
		return page.url.pathname === href || page.url.pathname.startsWith(href + "/");
	}

	const breadcrumbLabels: Record<string, string> = {
		portfolio: "Portfolio",
		team: "Leadership",
		calculator: "Investment Calculator",
		contact: "Contact",
		media: "Media",
		youtube: "YouTube",
		podcasts: "Podcasts",
		blog: "Blog"
	};

	const breadcrumbJsonLd = $derived.by(() => {
		const segments = page.url.pathname.split("/").filter(Boolean);
		if (segments.length === 0) return null;

		const items = [
			{
				"@type": "ListItem" as const,
				position: 1,
				name: "Home",
				item: "https://fergmarcapital.com/"
			}
		];

		let path = "";
		for (let i = 0; i < segments.length; i++) {
			path += `/${segments[i]}`;
			const label = breadcrumbLabels[segments[i]];
			if (!label) continue;
			items.push({
				"@type": "ListItem" as const,
				position: items.length + 1,
				name: label,
				item: `https://fergmarcapital.com${path}`
			});
		}

		return {
			"@context": "https://schema.org",
			"@type": "BreadcrumbList",
			itemListElement: items
		};
	});

	const logoUrl = cloudinaryUrl("brand/fergmar-capital/fergmar-capital-long-dark", {
		width: 300
	});
	const ogImageUrl = cloudinaryUrl("fergmarcapital-com/hero-cityscape", { width: 1200 });

	const organizationJsonLd = {
		"@context": "https://schema.org",
		"@type": "InvestmentCompany",
		name: "Fergmar Capital",
		legalName: "Fergmar Capital, LLC",
		url: "https://fergmarcapital.com",
		logo: logoUrl,
		image: ogImageUrl,
		description:
			"Fergmar Capital is a real estate investment management firm specializing in passive real estate syndications, multifamily, and commercial properties in Victoria, TX and growing secondary markets.",
		telephone: data.contactInfo.phone,
		email: data.contactInfo.email,
		address: {
			"@type": "PostalAddress",
			streetAddress: `${data.contactInfo.address.street}, ${data.contactInfo.address.suite}`,
			addressLocality: data.contactInfo.address.city,
			addressRegion: data.contactInfo.address.state,
			postalCode: data.contactInfo.address.zip,
			addressCountry: "US"
		},
		sameAs: [
			data.contactInfo.socialLinks.linkedin,
			data.contactInfo.socialLinks.facebook,
			data.contactInfo.socialLinks.youtube
		],
		openingHoursSpecification: {
			"@type": "OpeningHoursSpecification",
			dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
			opens: "08:00",
			closes: "17:00"
		},
		areaServed: {
			"@type": "City",
			name: "Victoria",
			containedInPlace: { "@type": "State", name: "Texas" }
		},
		founder: {
			"@type": "Person",
			name: "Brian H. Ferguson",
			jobTitle: "Founder & Managing Partner"
		}
	};
</script>

<svelte:head>
	<!-- Theme Overrides -->

	<style>
		:root {
			--primary: hsl(153, 63%, 28%) !important;
		}
		.dark {
			--primary: hsl(153, 63%, 28%) !important;
		}
	</style>

	<FaviconMeta faviconSlug="fergmar-capital" />

	<!-- Canonical URL -->
	<link rel="canonical" href="https://fergmarcapital.com{page.url.pathname}" />

	<!-- Open Graph defaults -->
	<meta property="og:url" content="https://fergmarcapital.com{page.url.pathname}" />
	<meta property="og:type" content="website" />
	<meta property="og:site_name" content="Fergmar Capital" />
	<meta property="og:locale" content="en_US" />
	<meta property="og:image" content={ogImageUrl} />
	<meta property="og:image:width" content="1200" />
	<meta property="og:image:height" content="630" />
	<meta
		property="og:image:alt"
		content="Fergmar Capital - Real Estate Investment Management, Victoria TX"
	/>

	<!-- Twitter Card defaults -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:site" content="@FergmarCapital" />
	<meta name="twitter:image" content={ogImageUrl} />

	{#if PUBLIC_NODE_ENV === "production"}
		<!-- Partytown config - forward necessary functions to web worker -->
		<script>
			partytown = {
				forward: ["dataLayer.push", "gtag"],
				resolveUrl: function (url, location, type) {
					if (type === "script" && !url.href.includes("/api/partytown-proxy")) {
						const proxyUrl = new URL("https://app.fergmar.com/api/partytown-proxy");
						proxyUrl.searchParams.append("url", url.href);
						return proxyUrl;
					}
					return url;
				}
			};
		</script>
		{@html partytownScript}

		<!-- Google Tag Manager via Partytown -->
		{@html `<script type="text/partytown">
			(function (w, d, s, l, i) {
				w[l] = w[l] || [];
				w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
				var f = d.getElementsByTagName(s)[0],
					j = d.createElement(s),
					dl = l != "dataLayer" ? "&l=" + l : "";
				j.async = true;
				j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
				f.parentNode.insertBefore(j, f);
			})(window, document, "script", "dataLayer", "GTM-KK772SRD");
		</script>`}
	{/if}

	<!-- Organization JSON-LD -->
	{@html `<script type="application/ld+json">${JSON.stringify(organizationJsonLd)}</script>`}

	<!-- Breadcrumb JSON-LD (sub-pages only) -->
	{#if breadcrumbJsonLd}
		{@html `<script type="application/ld+json">${JSON.stringify(breadcrumbJsonLd)}</script>`}
	{/if}
</svelte:head>

<div class="bg-background flex min-h-screen w-full flex-col">
	{#if PUBLIC_NODE_ENV === "production"}
		<!-- Google Tag Manager (noscript) - fallback for users without JS -->
		<noscript>
			<iframe
				src="https://www.googletagmanager.com/ns.html?id=GTM-KK772SRD"
				height="0"
				width="0"
				style="display:none;visibility:hidden"
				title="Google Tag Manager"
			></iframe>
		</noscript>
	{/if}
	<!-- Header -->
	<header class="border-background/10 bg-foreground fixed top-0 z-50 w-full border-b">
		<nav
			class="mx-auto flex max-w-5xl items-center justify-between px-6 py-4"
			aria-label="Global"
		>
			<!-- Logo -->
			<a href="/">
				<img
					src={logoUrl}
					alt="Fergmar Capital"
					class="h-12 w-auto"
					width="300"
					height="113"
				/>
			</a>

			<!-- Desktop Nav -->
			<div class="hidden items-center gap-8 md:flex">
				{#each data.menuItems as item}
					{#if item.children}
						<div class="group relative">
							<a
								href={item.href}
								class={cn(
									"inline-flex items-center gap-1 text-sm font-medium transition",
									isActive(item.href)
										? "text-background"
										: "text-background/70 hover:text-background"
								)}
							>
								{item.label}
								<ChevronDownIcon
									class="h-3.5 w-3.5 transition-transform group-hover:rotate-180"
								/>
							</a>
							<div
								class="pointer-events-none absolute top-full left-1/2 z-50 pt-2 opacity-0 transition-all group-hover:pointer-events-auto group-hover:opacity-100"
							>
								<div
									class="bg-foreground border-background/10 -translate-x-1/2 rounded-md border py-2 shadow-lg"
								>
									{#each item.children as child}
										<a
											href={child.href}
											class={cn(
												"block px-5 py-2 text-sm whitespace-nowrap transition",
												page.url.pathname === child.href
													? "text-background"
													: "text-background/60 hover:text-background hover:bg-background/5"
											)}
										>
											{child.label}
										</a>
									{/each}
								</div>
							</div>
						</div>
					{:else}
						<a
							href={item.href}
							class={cn(
								"text-sm font-medium transition",
								isActive(item.href)
									? "text-background"
									: "text-background/70 hover:text-background"
							)}
						>
							{item.label}
						</a>
					{/if}
				{/each}
				<Button
					href="https://fergmarcapital.invportal.com/login"
					size="sm"
					class="bg-primary hover:bg-primary/80 text-white"
				>
					Investor Portal
				</Button>
			</div>

			<!-- Mobile Menu Toggle -->
			<button
				class="text-background md:hidden"
				onclick={() => (mobileMenuOpen = !mobileMenuOpen)}
				aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
			>
				{#if mobileMenuOpen}
					<XIcon class="size-6" />
				{:else}
					<AlignJustifyIcon class="size-6" />
				{/if}
			</button>
		</nav>

		<!-- Mobile Menu -->
		{#if mobileMenuOpen}
			<div class="border-background/10 bg-foreground border-t px-6 py-6 md:hidden">
				<div class="flex flex-col gap-4">
					{#each data.menuItems as item}
						{#if item.children}
							<div class="flex flex-col">
								<div class="flex items-center justify-between">
									<a
										href={item.href}
										class="text-background/80 hover:text-background text-base font-medium"
										onclick={() => (mobileMenuOpen = false)}>{item.label}</a
									>

									<button
										class="text-background/60 hover:text-background p-1"
										onclick={() => (mobileMediaOpen = !mobileMediaOpen)}
										aria-label={mobileMediaOpen
											? "Collapse media links"
											: "Expand media links"}
									>
										<ChevronDownIcon
											class={cn(
												"h-4 w-4 transition-transform",
												mobileMediaOpen && "rotate-180"
											)}
										/>
									</button>
								</div>
								{#if mobileMediaOpen}
									<div
										class="border-background/10 mt-2 flex flex-col gap-3 border-l pl-4"
									>
										{#each item.children as child}
											<a
												href={child.href}
												class="text-background/60 hover:text-background text-sm"
												onclick={() => (mobileMenuOpen = false)}
												>{child.label}</a
											>
										{/each}
									</div>
								{/if}
							</div>
						{:else}
							<a
								href={item.href}
								class="text-background/80 hover:text-background text-base font-medium"
								onclick={() => (mobileMenuOpen = false)}>{item.label}</a
							>
						{/if}
					{/each}
					<Button
						href="https://fergmarcapital.invportal.com/login"
						size="sm"
						class="bg-primary hover:bg-primary/80 text-foreground mt-2 w-fit"
					>
						Investor Portal
					</Button>
				</div>
			</div>
		{/if}
	</header>

	<!-- Content -->
	<main class="grow">
		{@render children?.()}
	</main>

	<!-- Podcast Player (persistent across pages) -->
	<PodcastPlayer />

	<!-- Footer -->
	<FooterWrapper entityName="Fergmar Capital, LLC" disclaimer>
		<FooterRow class="py-12 md:py-16">
			<div class="grid w-full grid-cols-1 gap-10 sm:grid-cols-2 md:grid-cols-4">
				<FooterContactColumn
					{logoUrl}
					logoAlt="Fergmar Capital"
					logoClass="h-9"
					logoWidth={300}
					logoHeight={113}
					address={data.contactInfo.address}
					addressLink={data.contactInfo.addressLink}
					socialLinks={data.contactInfo.socialLinks}
				/>

				<nav class="flex flex-col gap-3" aria-label="Footer navigation">
					<p class="text-background text-sm font-semibold">Quick Links</p>
					{#each data.menuItems as item}
						<a
							href={item.href}
							class="text-background/50 hover:text-background text-sm transition"
						>
							{item.label}
						</a>
					{/each}
				</nav>

				<nav class="flex flex-col gap-3" aria-label="Media">
					<p class="text-background text-sm font-semibold">Media</p>
					<a
						href="/media"
						class="text-background/50 hover:text-background text-sm transition"
						>All Media</a
					>
					<a
						href="/media/youtube"
						class="text-background/50 hover:text-background text-sm transition"
						>Videos</a
					>
					<a
						href="/media/podcasts"
						class="text-background/50 hover:text-background text-sm transition"
						>Podcasts</a
					>
					<a
						href="/media/blog"
						class="text-background/50 hover:text-background text-sm transition">Blog</a
					>
				</nav>

				<div class="flex flex-col gap-3">
					<p class="text-background text-sm font-semibold">Get in Touch</p>
					<a
						href="tel:{data.contactInfo.phone}"
						class="text-background/50 hover:text-background text-sm transition"
					>
						{data.contactInfo.phone}
					</a>
					<a
						href="mailto:{data.contactInfo.email}"
						class="text-background/50 hover:text-background text-sm transition"
					>
						{data.contactInfo.email}
					</a>
					<Button
						href="https://fergmarcapital.invportal.com/login"
						size="sm"
						class="bg-primary hover:bg-primary/80 text-foreground mt-3 w-fit"
					>
						Investor Portal
					</Button>
				</div>
			</div>
		</FooterRow>
	</FooterWrapper>
</div>
